/**
 * 🌍 GLOBAL STANDART KOD (TÜM PROJELER İÇİN GEÇERLİDİR)
 *
 * Bu dosya QubePro ekosisteminde ortak (global) standarttır.
 * Tek kaynak `qubepro-core` reposudur; uydu repolarda kalıcı değişiklik yapılmaz.
 * Değişiklik gerekiyorsa Core'da güncellenir ve senkron ile diğer repolara yayılır.
 */

/**
 * Seçili firma (iş) kapsamı — tek kaynak: `SELECTED_COMPANY_STORAGE_KEY`.
 *
 * Token’daki `customerId` yalnızca hangi tenant veritabanına istek açılacağını belirler; `Company` satır id’si değildir.
 * UI’da seçilen iş firması ayrıca bu modülle (`X-Company-Id` başlığı) taşınır.
 *
 * Kurumsal başlık: `X-Company-Id` (diğer `X-*` response başlıklarıyla aynı aile).
 * Axios: `AuthContext` istek zincirinde `attachSelectedCompanyIdToRequest`.
 * `fetch`: `apps/web/src/api/axios.ts` global fetch sarmalayıcısı aynı başlığı ekler.
 */
import type { InternalAxiosRequestConfig } from 'axios';

import {
  isNotificationApiPath,
  resolveNotificationCompanyScopeId,
} from '../utils/notification-company-scope.utils';
import { getAppStorageKeyPrefix } from './app.config';
import { isApplicationBrowserAuthMode } from './auth-principal-mode.config';
import { transitionCompanyScope } from '../utils/company-scope-lifecycle.utils';

/** API ile aynı sabit (`apps/api/src/config/constants.ts` içindeki X_COMPANY_SCOPE_HEADER). */
export const COMPANY_SCOPE_HEADER = 'X-Company-Id';

/**
 * Bir isteğin hedef kapsamını çağıran ekran açıkça belirlediğinde kullanılacak kanonik değer.
 * Yetki kanıtı değildir; API bu kapsamı yine kullanıcı/service bağlamına göre doğrular.
 */
export function resolveExplicitCompanyScopeIdForRequest(
  value: string | number | bigint | null | undefined,
): string {
  const normalized = String(value ?? '').trim();
  return /^(?:0|[1-9]\d*)$/.test(normalized) ? normalized : '';
}

export const SELECTED_COMPANY_STORAGE_KEY = `${getAppStorageKeyPrefix()}_selected_company_id`;

/**
 * Workspace degisiminde eski tenant'a ait firma tercihi hicbir fallback yolundan
 * geri yuklenmemelidir. Ilk anahtar kanonik, digerleri legacy UI/settings
 * uyumluluk anahtarlaridir.
 */
export function clearSelectedCompanyScopeStorage(): void {
  const prefix = getAppStorageKeyPrefix();
  for (const key of [
    SELECTED_COMPANY_STORAGE_KEY,
    `${prefix}_selected_company`,
    'qubepro_userSetting_defaultCompanyId',
  ]) {
    try {
      localStorage.removeItem(key);
    } catch {
      // Storage engellense bile in-memory tenant/company temizligi devam eder.
    }
  }
}

let validatedSelectedCompanyIdForRequest: string | null = null;
const validatedCompanyScopeListeners = new Set<() => void>();

/**
 * API isteklerine yalnız Core firma listesiyle doğrulanmış seçim eklenir.
 * localStorage ve launch query değerleri sadece aday/tercihtir.
 */
export interface ValidatedCompanyScopeOptions {
  /** Yalniz CompanyContext tarafindan, system:admin yetkisi dogrulandiktan sonra kullanilir. */
  allowSystemScope?: boolean;
  /** Verified tenant/user identity, supplied by CompanyProvider; never a browser preference. */
  authorityKey?: string;
}

export function setValidatedSelectedCompanyIdForRequest(
  value: string | null | undefined,
  options: ValidatedCompanyScopeOptions = {},
): void {
  const normalized = String(value ?? '').trim();
  const nextValue = /^[1-9]\d*$/.test(normalized) || (normalized === '0' && options.allowSystemScope)
    ? normalized
    : null;
  const changed = validatedSelectedCompanyIdForRequest !== nextValue;
  validatedSelectedCompanyIdForRequest = nextValue;
  transitionCompanyScope(nextValue, options.authorityKey);
  if (!changed) return;
  validatedCompanyScopeListeners.forEach((listener) => listener());
}

export function getValidatedSelectedCompanyIdForRequest(): string | null {
  return validatedSelectedCompanyIdForRequest;
}

/** React/media tüketicileri doğrulanmış firma kapsamı hydrate olduğunda yeniden çözümleyebilir. */
export function subscribeValidatedSelectedCompanyIdForRequest(listener: () => void): () => void {
  validatedCompanyScopeListeners.add(listener);
  return () => validatedCompanyScopeListeners.delete(listener);
}

const EXEMPT_API_PREFIXES = [
  '/api/auth/',
  '/api/status',
  '/api/manifest',
  '/api/apps',
  '/api/settings/',
  '/api/users/me/companies',
  '/api/users/me/applications',
  '/api/core/',
  '/api/public/',
];

const CANON_HEADER_LOWER = COMPANY_SCOPE_HEADER.toLowerCase();

function normalizeRequestPath(url: string): string {
  const noQuery = url.split('?')[0] || '';
  try {
    if (/^https?:\/\//i.test(noQuery)) {
      return new URL(noQuery).pathname || '/';
    }
  } catch {
    /* URL ayrıştırılamadı; göreli yol denenir */
  }
  const basePath = (import.meta.env.VITE_BASE_PATH || '').replace(/\/+$/, '');
  let path = noQuery;
  if (basePath && path.startsWith(basePath)) {
    path = path.slice(basePath.length) || '/';
  }
  return path;
}

/** Pathname içinden `/api/...` kısmı (örn. `/core/api/x` → `/api/x`). */
function toCanonicalApiPath(pathname: string): string | null {
  const lower = pathname.toLowerCase();
  const needle = '/api/';
  const i = lower.indexOf(needle);
  if (i === -1) return null;
  return pathname.slice(i);
}

/** Göreli veya tam URL — firma başlığı eklenmesi gereken /api/* istekleri için true. */
export function pathRequiresSelectedCompanyOnRequest(url: string): boolean {
  // Application-space scope is carried only by the HttpOnly qpa-backed
  // server principal. Browser company headers are forbidden in this mode.
  if (isApplicationBrowserAuthMode()) return false;
  const path = normalizeRequestPath(url);
  const apiPath = toCanonicalApiPath(path);
  if (!apiPath) return false;
  const lower = apiPath.toLowerCase();
  // Browser yalniz CompanyContext tarafindan dogrulanmis BUSINESS kapsamını veya
  // desteklenen uygulamadaki system:admin kullanicinin sanal SYSTEM kapsamını tasir.
  // localStorage/header degeri tek basina yetki kaniti degildir.
  return !EXEMPT_API_PREFIXES.some((p) => lower.startsWith(p.toLowerCase()));
}

export function readSelectedCompanyIdFromStorage(): string | null {
  try {
    const primary = localStorage.getItem(SELECTED_COMPANY_STORAGE_KEY);
    if (primary?.trim()) return primary.trim();
  } catch {
    /* ignore */
  }
  return null;
}

/**
 * İstek için taşınacak kanonik iş firması kapsamı.
 *
 * Notification dahil tüm browser istekleri CompanyContext tarafından doğrulanmış
 * aktif kapsamı aynen taşır. Uygulama koduna göre kapsam üretilmez.
 */
export function resolveCompanyScopeIdForRequest(url: string): string | null {
  if (!pathRequiresSelectedCompanyOnRequest(url)) return null;

  const apiPath = toCanonicalApiPath(normalizeRequestPath(url));
  if (!apiPath) return null;

  const selectedCompanyId = getValidatedSelectedCompanyIdForRequest();
  if (isNotificationApiPath(apiPath)) {
    return resolveNotificationCompanyScopeId({
      selectedCompanyId,
    });
  }

  return selectedCompanyId;
}

/**
 * Axios `config.headers` bazen AxiosHeaders, Fetch API Headers, düz nesne veya dizi olabilir;
 * `instanceof AxiosHeaders` farklı bundle/çift axios ile güvenilmez; `forEach` yoksa düşmemek gerekir.
 */
function flattenHeadersRecord(headers: InternalAxiosRequestConfig['headers']): Record<string, string> {
  const out: Record<string, string> = {};
  if (headers == null) return out;

  const h = headers as Record<string, unknown> & {
    forEach?: (fn: (value: unknown, name: string) => void) => void;
  };

  if (typeof h.forEach === 'function') {
    try {
      h.forEach((value: unknown, name: string) => {
        if (value !== undefined && value !== null && String(value).trim() !== '') {
          out[String(name)] = String(value);
        }
      });
      return out;
    } catch {
      /* Headers / AxiosHeaders uyumsuz imza */
    }
  }

  if (Array.isArray(headers)) {
    for (const row of headers) {
      if (Array.isArray(row) && row.length >= 2) {
        const name = String(row[0]);
        const value = row[1];
        if (value !== undefined && value !== null && String(value).trim() !== '') {
          out[name] = String(value);
        }
      }
    }
    return out;
  }

  const plain = headers as Record<string, unknown>;
  for (const key of Object.keys(plain)) {
    const v = plain[key];
    if (v !== undefined && v !== null && typeof v !== 'object' && String(v).trim() !== '') {
      out[key] = String(v);
    }
  }
  return out;
}

/** İstek zaten firma kapsamı başlığı taşıyor mu (başlık adı büyük/küçük harf duyarsız). */
export function headersRecordHasCompanyScope(headers: Record<string, string>): boolean {
  return Object.keys(headers).some(
    (k) => k.toLowerCase() === CANON_HEADER_LOWER && String(headers[k]).trim() !== '',
  );
}

function requestAlreadyCarriesCompanyId(headers: InternalAxiosRequestConfig['headers']): boolean {
  if (!headers) return false;
  const h = headers as { get?: (name: string) => unknown };
  if (typeof h.get === 'function') {
    try {
      const v = h.get(COMPANY_SCOPE_HEADER) ?? h.get('x-company-id');
      if (v != null && String(v).trim() !== '') return true;
    } catch {
      /* düzleştirmeye düş */
    }
  }
  return headersRecordHasCompanyScope(flattenHeadersRecord(headers));
}

/** Actual wire scope wins over the current UI selection for explicitly scoped requests. */
export function readExplicitCompanyScopeHeader(headers: unknown): string | null {
  const record = flattenHeadersRecord(headers as InternalAxiosRequestConfig['headers']);
  const entry = Object.entries(record).find(([name]) => name.toLowerCase() === CANON_HEADER_LOWER);
  return entry?.[1]?.trim() || null;
}

export function attachSelectedCompanyIdToRequest(config: InternalAxiosRequestConfig): void {
  if (!pathRequiresSelectedCompanyOnRequest(config.url || '')) return;
  if (!config.headers || requestAlreadyCarriesCompanyId(config.headers)) return;

  const selectedId = resolveCompanyScopeIdForRequest(config.url || '');
  if (!selectedId) return;

  const hdrs = config.headers as {
    set?: (name: string, value: string) => void;
  };
  if (typeof hdrs.set === 'function') {
    hdrs.set(COMPANY_SCOPE_HEADER, selectedId);
  } else {
    (config.headers as Record<string, string>)[COMPANY_SCOPE_HEADER] = selectedId;
  }
}

function fetchInputUrl(input: RequestInfo | URL): string {
  if (typeof Request !== 'undefined' && input instanceof Request) return input.url;
  return String(input);
}

export function withSelectedCompanyScopeFetchInit(
  input: RequestInfo | URL,
  init?: RequestInit,
): RequestInit | undefined {
  const url = fetchInputUrl(input);
  if (!pathRequiresSelectedCompanyOnRequest(url)) return init;

  const selectedId = resolveCompanyScopeIdForRequest(url);
  if (!selectedId) return init;

  const baseHeaders =
    init?.headers ??
    (typeof Request !== 'undefined' && input instanceof Request ? input.headers : undefined);
  const headers = new Headers(baseHeaders);
  if (!headers.has(COMPANY_SCOPE_HEADER)) {
    headers.set(COMPANY_SCOPE_HEADER, selectedId);
  }

  return { ...(init || {}), headers };
}
